import { DestroyRef, Injectable, InjectionToken, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ReplaySubject, Subject } from 'rxjs';
import { SnackBarService } from '@/app/core/services/snack-bar.service';
import { PosthogService } from '@/app/core/telemetry/posthog.service';
import { environment } from '@/environments/environment';
import {
  notificationCategories,
  parseNotificationOpenUrl,
  type NotificationCategory,
  validateNotificationRoute,
} from '../../../service-worker-support';

const UPDATE_CHECK_INTERVAL_MS = 5 * 60 * 1000;
const OPEN_NOTIFICATION = 'OPEN_NOTIFICATION';
const WEB_PUSH_PRESENTED = 'WEB_PUSH_PRESENTED';
const DELIVERY_ID = /^[A-Za-z0-9._:-]{1,200}$/;
const PENDING_WEB_PUSH_OPEN_KEY = 'statusnow.pendingWebPushOpen';
const PENDING_WEB_PUSH_OPEN_MAX_FAILURES = 3;
const PENDING_WEB_PUSH_OPEN_TTL_MS = 60 * 60 * 1000;

export type WebPushPresentation = {
  category: NotificationCategory;
  deliveryId: string;
};

export type WebPushOpen = WebPushPresentation & {
  url: string;
};

type PendingWebPushOpen = {
  createdAt: number;
  failures: number;
  open: WebPushOpen;
};

type WorkboxLifecycleEvent = Event & {
  isUpdate?: boolean;
  sw?: ServiceWorker;
};

type WorkboxLike = {
  addEventListener(
    type: string,
    listener: (event: WorkboxLifecycleEvent) => void
  ): void;
  messageSkipWaiting(): void;
  register(): Promise<ServiceWorkerRegistration | undefined>;
  update(): Promise<void>;
};

type WorkboxFactory = () => Promise<WorkboxLike>;

export const PWA_WORKBOX_FACTORY = new InjectionToken<WorkboxFactory>(
  'PWA_WORKBOX_FACTORY',
  {
    providedIn: 'root',
    factory: () => async () => {
      const { Workbox } = await import('workbox-window');
      return new Workbox('/sw.js', {
        scope: '/',
        updateViaCache: 'none',
      }) as unknown as WorkboxLike;
    },
  }
);

export const PWA_RELOAD = new InjectionToken<() => void>('PWA_RELOAD', {
  providedIn: 'root',
  factory: () => () => globalThis.location.reload(),
});

@Injectable({ providedIn: 'root' })
export class PwaService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly factory = inject(PWA_WORKBOX_FACTORY);
  private readonly posthog = inject(PosthogService);
  private readonly reload = inject(PWA_RELOAD);
  private readonly router = inject(Router);
  private readonly snackBar = inject(SnackBarService);
  private readonly webPushPresentedSubject = new Subject<WebPushPresentation>();
  readonly webPushPresented$ = this.webPushPresentedSubject.asObservable();
  private readonly webPushOpenedSubject = new ReplaySubject<WebPushOpen>(1);
  readonly webPushOpened$ = this.webPushOpenedSubject.asObservable();
  private readonly activatingWorkers = new WeakSet<ServiceWorker>();
  private initialized = false;
  private lastUpdateCheck = 0;
  private registration?: ServiceWorkerRegistration;
  private reloadScheduled = false;
  private readonly promptedWorkers = new WeakSet<ServiceWorker>();
  private waitingWorker?: ServiceWorker;
  private workbox?: WorkboxLike;

  initialize(): void {
    if (
      this.initialized ||
      !environment.pwaEnabled ||
      typeof navigator === 'undefined' ||
      !('serviceWorker' in navigator)
    ) {
      return;
    }

    this.initialized = true;
    this.posthog.captureProductEvent('pwa launched', {
      display_mode: pwaDisplayMode(),
      was_online: navigator.onLine,
    });
    navigator.serviceWorker.addEventListener(
      'message',
      this.handleWorkerMessage
    );
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    globalThis.addEventListener('offline', this.handleOffline);
    globalThis.addEventListener('online', this.handleOnline);
    this.destroyRef.onDestroy(() => {
      navigator.serviceWorker.removeEventListener(
        'message',
        this.handleWorkerMessage
      );
      document.removeEventListener(
        'visibilitychange',
        this.handleVisibilityChange
      );
      globalThis.removeEventListener('offline', this.handleOffline);
      globalThis.removeEventListener('online', this.handleOnline);
    });

    if (!this.consumeNotificationOpenUrl()) {
      this.replayPendingWebPushOpen();
    }

    void this.registerWorker();
  }

  completeWebPushOpen(deliveryId: string): void {
    const pending = this.readPendingWebPushOpen();
    if (pending?.open.deliveryId === deliveryId) {
      this.removePendingWebPushOpen();
    }
  }

  deferWebPushOpen(deliveryId: string): void {
    const pending = this.readPendingWebPushOpen();
    if (!pending || pending.open.deliveryId !== deliveryId) {
      return;
    }

    pending.failures += 1;
    if (pending.failures >= PENDING_WEB_PUSH_OPEN_MAX_FAILURES) {
      this.removePendingWebPushOpen();
      return;
    }
    this.writePendingWebPushOpen(pending);
  }

  async checkForUpdate(): Promise<void> {
    if (!this.workbox) {
      return;
    }

    this.lastUpdateCheck = Date.now();
    try {
      await this.workbox.update();
    } catch (error: unknown) {
      this.captureException(error, 'update');
    }
  }

  private readonly handleVisibilityChange = (): void => {
    if (
      document.visibilityState !== 'visible' ||
      Date.now() - this.lastUpdateCheck < UPDATE_CHECK_INTERVAL_MS
    ) {
      return;
    }

    void this.checkForUpdate();
  };

  private readonly handleOffline = (): void => {
    this.posthog.captureProductEvent('app went offline');
  };

  private readonly handleOnline = (): void => {
    this.posthog.captureProductEvent('app recovered');
    this.replayPendingWebPushOpen();
  };

  private readonly handleWorkerMessage = (event: MessageEvent): void => {
    const message = asRecord(event.data);
    if (message?.['type'] === WEB_PUSH_PRESENTED) {
      const category = parseNotificationCategory(message['category']);
      const deliveryId = message['deliveryId'];
      if (
        category &&
        typeof deliveryId === 'string' &&
        DELIVERY_ID.test(deliveryId)
      ) {
        this.webPushPresentedSubject.next({ category, deliveryId });
      }
      return;
    }

    if (message?.['type'] !== OPEN_NOTIFICATION) {
      return;
    }

    const category =
      parseNotificationCategory(message['category']) ??
      notificationCategories.mention;
    const route = validateNotificationRoute(
      message['url'],
      globalThis.location.origin,
      category
    );
    if (route) {
      const deliveryId = message['deliveryId'];
      if (typeof deliveryId === 'string' && DELIVERY_ID.test(deliveryId)) {
        this.rememberWebPushOpen({ category, deliveryId, url: route });
      }
      this.posthog.captureProductEvent('push notification opened', {
        destination_feature:
          category === notificationCategories.mention
            ? 'shipment_notes'
            : category === notificationCategories.shipmentReminder
              ? 'shipment_details'
              : category === notificationCategories.exports
                ? 'shipment_exports'
                : 'smart_inbox',
      });
      void this.router.navigateByUrl(route);
    }
  };

  private consumeNotificationOpenUrl(): boolean {
    const parsed = parseNotificationOpenUrl(
      globalThis.location.href,
      globalThis.location.origin
    );
    if (!parsed) {
      return false;
    }

    try {
      globalThis.history.replaceState(
        globalThis.history.state,
        '',
        parsed.cleanUrl
      );
    } catch {
      // Cleaning the address bar is best-effort; acknowledgement takes priority.
    }

    if (!parsed.notification) {
      return false;
    }

    this.rememberWebPushOpen(parsed.notification);
    return true;
  }

  private rememberWebPushOpen(open: WebPushOpen): void {
    const existing = this.readPendingWebPushOpen();
    const pending: PendingWebPushOpen =
      existing?.open.deliveryId === open.deliveryId
        ? { ...existing, open }
        : { createdAt: Date.now(), failures: 0, open };
    this.writePendingWebPushOpen(pending);
    this.webPushOpenedSubject.next(open);
  }

  private replayPendingWebPushOpen(): void {
    const pending = this.readPendingWebPushOpen();
    if (pending) {
      this.webPushOpenedSubject.next(pending.open);
    }
  }

  private readPendingWebPushOpen(): PendingWebPushOpen | null {
    try {
      const pending = asRecord(
        JSON.parse(
          globalThis.sessionStorage.getItem(PENDING_WEB_PUSH_OPEN_KEY) ?? 'null'
        )
      );
      const open = asRecord(pending?.['open']);
      const category = parseNotificationCategory(open?.['category']);
      const deliveryId = open?.['deliveryId'];
      const createdAt = pending?.['createdAt'];
      const failures = pending?.['failures'];
      const route = category
        ? validateNotificationRoute(
            open?.['url'],
            globalThis.location.origin,
            category
          )
        : null;
      if (
        typeof createdAt !== 'number' ||
        Date.now() - createdAt > PENDING_WEB_PUSH_OPEN_TTL_MS ||
        typeof failures !== 'number' ||
        failures < 0 ||
        failures >= PENDING_WEB_PUSH_OPEN_MAX_FAILURES ||
        !category ||
        typeof deliveryId !== 'string' ||
        !DELIVERY_ID.test(deliveryId) ||
        !route
      ) {
        this.removePendingWebPushOpen();
        return null;
      }

      return {
        createdAt,
        failures,
        open: { category, deliveryId, url: route },
      };
    } catch {
      this.removePendingWebPushOpen();
      return null;
    }
  }

  private writePendingWebPushOpen(pending: PendingWebPushOpen): void {
    try {
      globalThis.sessionStorage.setItem(
        PENDING_WEB_PUSH_OPEN_KEY,
        JSON.stringify(pending)
      );
    } catch {
      // Storage can be unavailable in restricted browser modes.
    }
  }

  private removePendingWebPushOpen(): void {
    try {
      globalThis.sessionStorage.removeItem(PENDING_WEB_PUSH_OPEN_KEY);
    } catch {
      // Storage can be unavailable in restricted browser modes.
    }
  }

  private async registerWorker(): Promise<void> {
    try {
      this.workbox = await this.factory();
      this.workbox.addEventListener('waiting', (event) => {
        if (event.isUpdate === false) {
          return;
        }

        this.waitingWorker =
          event.sw ?? this.registration?.waiting ?? undefined;
        if (this.waitingWorker) {
          this.showUpdateReady(this.waitingWorker);
        }
      });

      await waitForWindowLoad();
      this.registration = await this.workbox.register();
      this.posthog.capture('pwa_worker_registered');

      if (this.registration?.waiting) {
        this.waitingWorker = this.registration.waiting;
        this.showUpdateReady(this.waitingWorker);
      }

      await this.checkForUpdate();
    } catch (error: unknown) {
      this.captureException(error, 'register');
    }
  }

  private showUpdateReady(worker: ServiceWorker): void {
    if (this.promptedWorkers.has(worker)) {
      return;
    }

    this.promptedWorkers.add(worker);
    this.posthog.capture('pwa_update_waiting');
    this.snackBar.info('New version ready', {
      action: {
        label: 'Reload now',
        onAction: () => void this.acceptUpdate(),
      },
      dismissible: true,
      duration: 0,
      message: 'Save your work before loading the latest StatusNow version.',
    });
  }

  private async acceptUpdate(): Promise<void> {
    const worker = this.waitingWorker;
    if (!worker || !this.workbox) {
      return;
    }

    if (this.activatingWorkers.has(worker)) {
      return;
    }

    try {
      this.activatingWorkers.add(worker);
      this.posthog.capture('pwa_update_accepted');
      worker.addEventListener('statechange', () => {
        if (worker.state === 'activated') {
          this.reloadAfterActivation();
        }
      });
      this.workbox.messageSkipWaiting();

      if (worker.state === 'activated') {
        this.reloadAfterActivation();
      }
    } catch (error: unknown) {
      this.activatingWorkers.delete(worker);
      this.captureException(error, 'activate');
    }
  }

  private reloadAfterActivation(): void {
    if (this.reloadScheduled) {
      return;
    }

    this.reloadScheduled = true;
    this.posthog.capture('pwa_update_activated');
    this.reload();
  }

  private captureException(error: unknown, stage: string): void {
    this.posthog.captureException(error, {
      pwaStage: stage,
      route: undefined,
    });
  }
}

export function pwaDisplayMode(): string {
  if (typeof matchMedia !== 'function') {
    return 'browser';
  }
  for (const mode of ['fullscreen', 'standalone', 'minimal-ui'] as const) {
    if (matchMedia(`(display-mode: ${mode})`).matches) {
      return mode;
    }
  }
  return 'browser';
}

function waitForWindowLoad(): Promise<void> {
  if (document.readyState === 'complete') {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    globalThis.addEventListener('load', () => resolve(), { once: true });
  });
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : null;
}

function parseNotificationCategory(
  value: unknown
): NotificationCategory | null {
  return value === notificationCategories.exports ||
    value === notificationCategories.mention ||
    value === notificationCategories.shipmentReminder ||
    value === notificationCategories.smartInboxQuoteGeneration
    ? value
    : null;
}
